/**
 * Getting Started ribbon (P106; P214.5 Observatory restyle).
 *
 * The single daylight ribbon at the top of Home. Two pieces share the
 * row:
 *   - the onboarding nudge (heading + progress bar + "N of M steps" +
 *     "Continue setup ->"), shown ONLY while the bank is `fresh`;
 *   - an optional `trailing` slot (the position-queue pills, light
 *     tone), pushed to the end.
 *
 * Render shapes:
 *   fresh                -> full brassTint ribbon (onboarding + trailing).
 *   not fresh + trailing -> a borderless right-aligned queue row.
 *   not fresh, no slot   -> nothing.
 */
import { useEffect, type ReactNode } from "react";
import { Arrow } from "shared/components/Arrow";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { StepProgress } from "shared/components/StepProgress";

import { useOnboardingStatus } from "shared/api/useOnboarding";
import { useMe } from "shared/api/useMe";
import { canRunOperations } from "shared/api/authority";
import { isMyStation } from "shared/journey/authority";
import { STEP_META } from "shared/journey/stepMeta";
import { useJourneySpineStatus } from "shared/journey/useJourney";
import {
  readLastKnownSetup,
  writeLastKnownSetup,
} from "shared/onboarding/lastKnownSetup";

export function GettingStartedTeaser({ trailing }: { trailing?: ReactNode } = {}) {
  const { t } = useTranslation();
  const status = useOnboardingStatus();
  const me = useMe();
  const journey = useJourneySpineStatus();

  /* Round 11 B3 -- second time this control has gone missing.
     Until the status answers there is no ribbon at all, so on a cold
     load the administrator's own next step is absent from the screen
     he starts on and the walk falls back to the nav. Render what this
     browser last saw and let the answer correct it; a guess would be
     worse than nothing, a memory is not a guess. */
  const remembered = readLastKnownSetup(me.data?.subject);
  useEffect(() => {
    if (!status.data || !me.data?.subject) return;
    const previous = readLastKnownSetup(me.data.subject);
    writeLastKnownSetup(me.data.subject, {
      show_getting_started: status.data.show_getting_started,
      completed: status.data.completed,
      total: status.data.total,
      ...(previous?.handing_over === undefined
        ? {}
        : { handing_over: previous.handing_over }),
    });
  }, [status.data, me.data?.subject]);
  /* Round 17 G20 -- and the verdict on THIS person's stations is
     remembered with the figures, so the next cold load says what this
     one found rather than drawing a finished setup as a ladder while
     the journey is in flight. */
  const stationsLeft = journey.isSuccess
    ? (journey.data?.steps ?? []).filter((s) => {
        const meta = STEP_META[s.key];
        return meta ? isMyStation(meta.owner, me.data) && s.status !== "done" : false;
      }).length
    : null;
  useEffect(() => {
    if (stationsLeft === null || !status.data || !me.data?.subject) return;
    writeLastKnownSetup(me.data.subject, {
      show_getting_started: status.data.show_getting_started,
      completed: status.data.completed,
      total: status.data.total,
      handing_over: stationsLeft === 0,
    });
  }, [stationsLeft, status.data, me.data?.subject]);
  const known = status.data ?? remembered;
  const fresh = known?.show_getting_started ?? false;

  // E5 -- this greeted the preparer, the reviewer and the CRO with
  // "New to MARSAD? Finish setting up" and "3 steps left before hand
  // over". Round 2 narrowed WHICH steps it counts, and the framing is
  // still the administrator's: standing a bank up and handing it over
  // is a job two seats hold. Everyone else meets the product already
  // running, and being told to finish setting it up is an instruction
  // they cannot act on.
  //
  // Their own remaining work is on /journey, which is a permanent
  // surface rather than a setup nudge.
  const setupIsMine = canRunOperations(me.data);

  if (!fresh || !setupIsMine) {
    // Onboarding is done -- no brassTint nudge. If the queue pills were
    // handed in, surface them on their own, end-aligned; otherwise
    // render nothing (the ribbon disappears entirely).
    if (!trailing) return null;
    return <div className="flex items-center justify-end">{trailing}</div>;
  }

  // E1 -- everyone saw the same ladder and the same count. The
  // administrator's banner sent him to the ops-res governance steps
  // ("6 of 7") while his own strip said "1 step left", and a preparer
  // was told to "continue setup" on work no position of his owns.
  // Count the steps that are actually the viewer's, from the one
  // journey source all three counters already read.
  const myStations = (journey.data?.steps ?? []).filter((s) => {
    const meta = STEP_META[s.key];
    return meta ? isMyStation(meta.owner, me.data) : false;
  });
  const myDone = myStations.filter((s) => s.status === "done").length;
  const remaining = myStations.length - myDone;
  /* Round 12 F1, third appearance of this fix and the first time it has
     LIED. Twelve deliberate loads of an identical estate with two setup
     steps outstanding: five read "1 step left before hand over -- 6 of
     7 steps complete" with a working link; five read "Your setup is
     done: hand over to the resilience lead" with no link and no Getting
     Started in the nav; two rendered nothing.
     
     The cause is one word. `myStations` is [] while the journey query
     is in flight, so `remaining` is 0, so `handingOver` is TRUE -- and
     an administrator was told his setup was finished by a component
     that had not yet asked. The figures fall back to the remembered
     bank-wide ones, which is why five of the loads looked right: those
     are the ones where the journey happened to have landed.
     
     A count of nothing is not a count of zero. Every claim below waits
     for the answer. */
  const stationsKnown = journey.isSuccess;

  // Nobody's steps -> no nudge. Telling a preparer to finish setting
  // up a bank, on a ladder holding nothing he can do, is an
  // instruction he cannot act on.
  if (journey.isSuccess && myStations.length === 0) {
    return trailing ? (
      <div className="flex items-center justify-end">{trailing}</div>
    ) : null;
  }

  // Fall back to the bank-wide figures until the journey lands, so the
  // ribbon does not flicker between two counts.
  const completed = stationsKnown ? myDone : (known?.completed ?? 0);
  const total = stationsKnown ? myStations.length : (known?.total ?? 0);
  /* F1 -- "how many are left" drives the headline, the link and its
     label, and while the stations are unknown the honest answer is the
     remembered one. Taking it from an empty list said "done" three
     different ways on a bank with two steps outstanding. */
  const left = stationsKnown ? remaining : Math.max(0, total - completed);
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  // Where THIS person's remaining steps live.
  const target = myStations.find((s) => s.status !== "done")?.key;
  // D1 -- with all seven done the banner still read "New to MARSAD?
  // Finish setting up" and sent the administrator back to the ladder he
  // had just finished. A done state that reads like an unstarted one
  // tells the person who did the work that it did not register.
  //
  // What follows setup is a hand-over: the ladder from here is the
  // resilience lead's, so the link is HER stations rather than a
  // twenty-step list the reader has to sort through.
  // F1 -- the hand-over is a CLAIM about finished work, so it waits for
  // the list it counts. Until then the ribbon carries the remembered
  // figures and says nothing about being done, which is what it knows.
  const handingOver = stationsKnown
    ? remaining === 0 && (me.data?.is_admin ?? false)
    : (remembered?.handing_over ?? false) && (me.data?.is_admin ?? false);
  const to = handingOver
    ? "/admin/setup?owner=opsres"
    : (target && STEP_META[target]?.route) || "/admin/setup";

  return (
    <div
      data-testid="getting-started-teaser"
      /* ROUND 13 G3 -- an unwrappable row 550px wide dragged Home
          sideways by 637px on a 375px screen. */
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[9px] border border-brand-brassLine bg-brand-brassTint px-[18px] py-[13px] text-[13px]"
    >
      <b className="whitespace-nowrap font-semibold text-ink">
        {left > 0
          ? t("onboarding.teaser.headingMine", { count: left })
          : handingOver
            ? t("onboarding.teaser.handOver")
            : t("onboarding.teaser.doneMine")}
      </b>
      <span
        className="h-[5px] w-[110px] overflow-hidden rounded-[3px] bg-brand-brassLine"
        aria-hidden="true"
      >
        <span className="block h-full bg-brand-brass" style={{ width: `${pct}%` }} />
      </span>
      <StepProgress
        testid="teaser-progress"
        done={completed}
        total={total}
        className="text-xs text-ink-muted"
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:ms-auto sm:gap-8">
        {trailing}
        <Link
          to={to}
          className="whitespace-nowrap text-[12.5px] font-semibold text-brand-brassDark hover:underline"
        >
          {left === 0
            ? handingOver
              ? t("onboarding.teaser.handOverCta")
              : t("onboarding.teaser.doneCta")
            : t("onboarding.teaser.cta")}{" "}
          <Arrow />
        </Link>
      </div>
    </div>
  );
}
